/**
 * Maps frontend route paths → page IDs (the same IDs the backend exposes via
 * /api/auth/me.allPages). Used by ProtectedRoute when enforcement is on.
 *
 * Mirrors server/src/config/pagePaths.ts and keeps both sides in lockstep.
 *
 * Rules:
 *  - Patterns are checked in order; first match wins
 *  - pageId === null → always allowed (landing pages, admin panel, shared)
 *  - No match → always allowed (fail open; matches backend behavior)
 */

export interface RoutePageRule {
  pattern: RegExp;
  pageId: string | null;
}

export const ROUTE_PAGE_RULES: RoutePageRule[] = [
  // Always allowed
  { pattern: /^\/$/, pageId: null },
  { pattern: /^\/login(\/|$)/, pageId: null },
  { pattern: /^\/admin(\/|$)/, pageId: null },           // super-admin-only via separate check
  { pattern: /^\/app-prioritization(\/|$)/, pageId: null }, // super-admin-only via layout check
  { pattern: /^\/engineering(\/|$)/, pageId: null },   // email allowlist via layout check (canSeeEngineering)
  { pattern: /^\/fast-track(\/|$)/, pageId: "platform.fast_track_centralization" }, // Fast Track Centralization (mirrors server pagePaths)
  { pattern: /^\/tofu(\/|$)/, pageId: "tofu.job_management" }, // ToFu Job Management (mirrors server pagePaths)
  // Sales app (New Opportunity flow) — creates opportunities, so it rides the
  // tracker's grant; anyone who can use the opps tracker can use Sales.
  { pattern: /^\/sales(\/|$)/, pageId: "platform.opportunities" },
  { pattern: /^\/no-access(\/|$)/, pageId: null },

  // Talent
  { pattern: /^\/talent\/?$/, pageId: "talent.dashboard" },
  { pattern: /^\/talent\/active(\/|$)/, pageId: "talent.active" },
  { pattern: /^\/talent\/matched(\/|$)/, pageId: "talent.matched" },
  { pattern: /^\/talent\/all(\/|$)/, pageId: "talent.all" },
  { pattern: /^\/talent\/applicants(\/|$)/, pageId: "talent.applicants" },
  { pattern: /^\/talent\/interviews(\/|$)/, pageId: "talent.interviews" },
  { pattern: /^\/talent\/fast-track(\/|$)/, pageId: "talent.fast_track" },
  { pattern: /^\/talent\/onboarding(\/|$)/, pageId: "talent.onboarding" },
  { pattern: /^\/talent\/rejected(\/|$)/, pageId: "talent.cooldown" },
  { pattern: /^\/talent\/inactive(\/|$)/, pageId: "talent.inactive" },
  { pattern: /^\/talent\/passive(\/|$)/, pageId: "talent.passive" },
  { pattern: /^\/talent\/scheduled-calls(\/|$)/, pageId: "talent.scheduled_calls" },
  { pattern: /^\/talent\/detective(\/|$)/, pageId: "talent.detective" },
  { pattern: /^\/talent\/pipeline-universe(\/|$)/, pageId: "talent.pipeline_universe" },
  { pattern: /^\/talent\/(webhook-logs|system-logs)(\/|$)/, pageId: "talent.logs" },
  { pattern: /^\/talent\/reports(\/|$)/, pageId: "talent.reports" },
  { pattern: /^\/talent\/recruiter-dashboard(\?|\/|$)/, pageId: "recruiter.dashboard" },
  { pattern: /^\/talent\/profile(\/|$)/, pageId: "talent.profile" },

  // Platform
  { pattern: /^\/platform\/?$/, pageId: "platform.dashboard" },
  { pattern: /^\/platform\/dashboard\/executive(\/|$)/, pageId: "platform.dashboard_executive" },
  { pattern: /^\/platform\/dashboard\/specialist(\/|$)/, pageId: "platform.dashboard_specialist" },
  { pattern: /^\/platform\/(company-manager|company-profile)(\/|$)/, pageId: "platform.companies" },
  { pattern: /^\/platform\/(positions|candidates-in-process)(\/|$)/, pageId: "platform.positions" },
  // Archive page sits under /opportunities/archive but is gated by its
  // own page id so a role can have the tracker without the archive view.
  { pattern: /^\/platform\/opportunities\/archive(\/|$)/, pageId: "platform.opportunities_archive" },
  // Monthly OKR report — purely an analytics view of the same opp data.
  // Gated under the executive dashboard pageId so anyone with access to
  // Executive Overview also sees this without a separate role grant.
  { pattern: /^\/platform\/opportunities-monthly(\/|$)/, pageId: "platform.dashboard_executive" },
  { pattern: /^\/platform\/opportunities(\/|$)/, pageId: "platform.opportunities" },
  { pattern: /^\/platform\/prioritization(\/|$)/, pageId: "platform.prioritization" },
  { pattern: /^\/platform\/activity(\/|$)/, pageId: "platform.activity" },
  { pattern: /^\/platform\/match-intelligence(\/|$)/, pageId: "platform.match_intelligence" },
  { pattern: /^\/platform\/supply-demand(\/|$)/, pageId: "platform.supply_demand" },
  { pattern: /^\/platform\/semantic-graph(\/|$)/, pageId: "platform.semantic_graph" },
  { pattern: /^\/platform\/talent-emails(\/|$)/, pageId: "platform.opportunities" },

  // Campaigns
  { pattern: /^\/campaigns\/?$/, pageId: "campaigns.dashboard" },
  { pattern: /^\/campaigns\/monitoring(\/|$)/, pageId: "campaigns.monitoring" },
  // A/B Tests — read-only analytics over MOFU/BOFU sends; gated under the
  // campaign dashboard pageId so anyone with campaign-analytics access sees
  // it without a separate role grant (same precedent as opportunities-monthly).
  { pattern: /^\/campaigns\/ab-tests(\/|$)/, pageId: "campaigns.dashboard" },
  // Campaign Flows — edits the same MOFU/BOFU config the MOFU tab edits (the
  // backing API is /api/drip-campaigns/mofu/config, gated CAMPAIGNS_MOFU
  // server-side), so it shares the MOFU pageId rather than a new grant.
  { pattern: /^\/campaigns\/flows(\/|$)/, pageId: "campaigns.mofu" },
  { pattern: /^\/campaigns\/mofu(\/|$)/, pageId: "campaigns.mofu" },
  { pattern: /^\/campaigns\/bofu(\/|$)/, pageId: "campaigns.bofu" },
  { pattern: /^\/campaigns\/whatsapp(\/|$)/, pageId: "campaigns.whatsapp" },
  // Interview reminders — gated under the campaign dashboard pageId so anyone with
  // campaign access sees it without a separate role grant (same precedent as ab-tests).
  { pattern: /^\/campaigns\/reminders(\/|$)/, pageId: "campaigns.dashboard" },
  { pattern: /^\/campaigns\/sender-metrics(\/|$)/, pageId: "campaigns.sender_metrics" },

  // Customer Success
  { pattern: /^\/cs\/?$/, pageId: "cs.dashboard" },
  { pattern: /^\/cs\/companies(\/|$)/, pageId: "cs.companies" },
  { pattern: /^\/cs\/contracts(\/|$)/, pageId: "cs.contracts" },
  // The CSV-only invoice page shares the CS contracts permission. Creating
  // Stripe drafts additionally requires the `cs.invoicing.create_drafts`
  // capability (enforced in the backend regardless of the enforcement toggle,
  // and mirrored in the UI so the button explains itself).
  { pattern: /^\/cs\/invoicing(\/|$)/, pageId: "cs.contracts" },
  { pattern: /^\/cs\/talent(\/|$)/, pageId: "cs.talent" },
  // Talent Hotline — open to anyone who can open the CS app.
  { pattern: /^\/cs\/hotline(\/|$)/, pageId: "cs.dashboard" },
  // The review queue and the contract-events page read contract change events,
  // so they ride on the contracts grant. Integrations and the worksheet importer
  // are still static "coming soon" pages on existing permissions.
  { pattern: /^\/cs\/ongoing-review(\/|$)/, pageId: "cs.contracts" },
  { pattern: /^\/cs\/contract-events(\/|$)/, pageId: "cs.contracts" },
  { pattern: /^\/cs\/integrations(\/|$)/, pageId: "cs.contracts" },
  { pattern: /^\/cs\/worksheet-import(\/|$)/, pageId: "cs.contracts" },
  { pattern: /^\/cs\/next-actions(\/|$)/, pageId: "cs.issues" },
  { pattern: /^\/cs\/issues(\/|$)/, pageId: "cs.issues" },
  { pattern: /^\/cs\/upsells(\/|$)/, pageId: "cs.upsells" },
  { pattern: /^\/cs\/email-check-ins(\/|$)/, pageId: "cs.email_checkins" },
  { pattern: /^\/cs\/reports(\/|$)/, pageId: "cs.reports" },
  // CS runtime settings (reason codes, thresholds, upsell weights). Reads ride
  // on the contracts permission; WRITES are gated by the `cs.settings.edit`
  // capability (enforced server-side on PUT /api/cs/settings regardless of the
  // enforcement toggle, mirrored in the UI via useHasCapability).
  { pattern: /^\/cs\/settings(\/|$)/, pageId: "cs.contracts" },

  // Agent Workflow — index route renders AgentInboxPage, so gate it as
  // agentworkflow.inbox (no standalone dashboard page exists today).
  { pattern: /^\/agent-workflow\/?$/, pageId: "agentworkflow.inbox" },
  { pattern: /^\/agent-workflow\/inbox(\/|$)/, pageId: "agentworkflow.inbox" },
  { pattern: /^\/agent-workflow\/needs-attention(\/|$)/, pageId: "agentworkflow.needs_attention" },
  // The Escalations dashboard reads the same escalations, so it rides on the Needs Attention grant.
  { pattern: /^\/agent-workflow\/escalations(\/|$)/, pageId: "agentworkflow.needs_attention" },
  { pattern: /^\/agent-workflow\/agents(\/|$)/, pageId: "agentworkflow.agents" },
  { pattern: /^\/agent-workflow\/context-maps(\/|$)/, pageId: "agentworkflow.context_maps" },
  { pattern: /^\/agent-workflow\/routing-rules(\/|$)/, pageId: "agentworkflow.routing_rules" },
  { pattern: /^\/agent-workflow\/tools(\/|$)/, pageId: "agentworkflow.tools" },
  { pattern: /^\/agent-workflow\/models(\/|$)/, pageId: "agentworkflow.models" },
];

export function resolvePageForPath(pathname: string): string | null | undefined {
  for (const rule of ROUTE_PAGE_RULES) {
    if (rule.pattern.test(pathname)) return rule.pageId;
  }
  return undefined;
}
